import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../App';
import AuthRepository from '../../domain/repositories/AuthRepository';
import '../../assets/css/login.css';
import backgroundImage from '../../assets/images/login-background.png';
import ecmIcon from '../../assets/images/ecm-logo.png';
import { LOGIN_MESSAGE } from '../../utils/constants/message_constants';
import { validateName, validatePassword } from '../../utils/validation';

function LoginForm() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const authRepository = new AuthRepository();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Preserve the URL the user was trying to access
  const from = location.state?.from?.pathname || '/';

  const handleLogin = async (event) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      setError('');

      const errors = [];
      if (!validateName(username)) {
        errors.push(LOGIN_MESSAGE.USERNAME_VALID);
      }
      if (!validatePassword(password)) {
        errors.push(LOGIN_MESSAGE.PASSWORD_VALID);
      }

      if (errors.length > 0) {
        setError(errors.join(' '));
        setIsSubmitting(false);
        return;
      }

      // Authenticate with the backend
      await authRepository.login(username, password);
      // Update app state to reflect successful login
      login();
      // Clear form
      setUsername('');
      setPassword('');
      // Redirect to the intended page
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      setError('Invalid username or password. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Since apiClient.js uses LocalStorageService, assume AuthRepository checks tokens there
  // If you still want to check sessionStorage, you can uncomment this block
  /*
  const accessToken = sessionStorage.getItem('access_token');
  if (accessToken) {
    return (
      <div>
        <p>You are logged in.</p>
        <button
          onClick={() => {
            sessionStorage.removeItem('access_token');
            navigate('/login', { replace: true });
          }}
        >
          Logout
        </button>
      </div>
    );
  }
  */
  // Alternatively, rely on AuthContext's isAuthenticated
  const { isAuthenticated } = useContext(AuthContext);
  if (isAuthenticated) {
    return null; // App.js will redirect to '/' or 'from' via the Route
  }

  return (
    <div
      className="login-page"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header className="login-header">
        <div className="header-left">
          <img src={ecmIcon} alt="Eisteddfod Logo" className="header-logo" />
          <h1>Eisteddfod Cert Manager</h1>
        </div>
      </header>

      {/* Form Section */}
      <div className="form-section">
        <img src={ecmIcon} alt="Eisteddfod Logo" className="form-logo" />
        <div className="form-wrapper">
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
              />
            </div>
            <div className="form-group">
              <button
                type="submit"
                className="login-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing In...' : 'SIGN IN'}
              </button>
            </div>
            {isSubmitting && <div className="spinner"></div>}
            {error && <p className="error-message">{error}</p>}
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="login-footer">
        <p className="copyright">
          © 2025 built by Avance Business Technology
        </p>
      </footer>
    </div>
  );
}

export default LoginForm;